import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Nummernkreis } from "@kundeninfo/api-client";
import { Feld, useBeforeUnload } from "@kundeninfo/ui";
import { ENTITY_LABEL, KEINE_ZAHL, Meldung, alsZahl, feldFehlerAus, formatKonfliktZeit,
         meldungAus } from "./gemeinsam.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich NUMMERNKREISE.
 *
 * Herausgelöst aus views/Verwaltung.tsx (Etappe 2, Aufgabe 3) — rein strukturell, CSS und
 * Verhalten unverändert (Block `vw__`, views/Verwaltung.css).
 *
 * **Ohne zweite Ebene** (Entscheid Aufgabe 3, so schon im Aufgabenzuschnitt vorgesehen):
 * Die Kreise entstehen nicht und vergehen nicht — es gibt je Unternehmen genau vier, fest
 * angelegt mit dem Unternehmen. Der Bereich ist damit eine TABELLE mit festem Umfang, deren
 * Sinn gerade der Überblick über alle Kreise nebeneinander ist („welches Unternehmen
 * nummeriert wie?"). Es gibt weder Anlegen noch Löschen, für die eine Liste da wäre, und
 * ein Detail hinter einem Klick zeigte genau die zwei Felder, die schon in der Zeile stehen.
 */

/** Nur die drei Wege dieses Bereichs — so lässt er sich im Test mit drei vi.fn zeichnen. */
export type NummernkreiseClient = Pick<ApiClient,
  "listNumberRanges" | "updateNumberRangePattern" | "setNumberRangeStart">;

function alsNummernkreis(wert: unknown): Nummernkreis | null {
  if (typeof wert !== "object" || wert === null) return null;
  const n = wert as Partial<Nummernkreis>;
  if (typeof n.id !== "string" || typeof n.updatedAt !== "string") return null;
  return wert as Nummernkreis;
}

export function NummernkreiseBereich(
  { client }: { client: NummernkreiseClient },
): ReactElement {
  const [zeilen, setZeilen] = useState<Nummernkreis[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Record<string, string>>({});
  const [feldFehler, setFeldFehler] = useState<Record<string, string>>({});
  const [meldungen, setMeldungen] = useState<Record<string, string>>({});
  const [konflikte, setKonflikte] =
    useState<Record<string, { von: string; wann: string; fremd: Nummernkreis | null }>>({});
  const [speichert, setSpeichert] = useState<string | null>(null);
  /** Der getippte Startwert je Kreis — getrennt vom Muster-Entwurf, weil beide Felder
   *  getrennt gespeichert werden (siehe `startwertSpeichern`). */
  const [startEntwuerfe, setStartEntwuerfe] = useState<Record<string, string>>({});
  const [startFehler, setStartFehler] = useState<Record<string, string>>({});

  const listNumberRanges = client.listNumberRanges;

  useEffect(() => {
    let abgebrochen = false;
    listNumberRanges()
      .then((antwort) => { if (!abgebrochen) setZeilen(antwort.items); })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(
            meldungAus(fehler, "Die Nummernkreise konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [listNumberRanges]);

  function musterVon(z: Nummernkreis): string {
    return entwuerfe[z.id] ?? z.pattern;
  }

  /** Wie im Benutzerbereich: irgendein Muster weicht vom gespeicherten ab (2a-Kleinpunkt). */
  const etwasGeaendert = (zeilen ?? []).some((z) => musterVon(z) !== z.pattern);
  useBeforeUnload(etwasGeaendert);

  function vergiss(id: string): void {
    setEntwuerfe((e) => { const rest = { ...e }; delete rest[id]; return rest; });
  }

  async function speichern(z: Nummernkreis): Promise<void> {
    setFeldFehler((f) => ({ ...f, [z.id]: "" }));
    setMeldungen((m) => ({ ...m, [z.id]: "" }));
    setKonflikte((k) => { const rest = { ...k }; delete rest[z.id]; return rest; });
    setSpeichert(z.id);
    try {
      // Nummernkreise tragen keine `version`; verglichen wird der Zeitstempel des
      // zuletzt bekannten Standes (Entscheidung C). Er kommt aus der Zeile — und die
      // Zeile wird unten durch die ANTWORT ersetzt, sonst schlüge das zweite Speichern
      // mit 409 fehl, obwohl niemand dazwischenkam.
      const neu = await client.updateNumberRangePattern(z.id, z.updatedAt, musterVon(z));
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === neu.id ? neu : x))));
      vergiss(z.id);
    } catch (fehler) {
      // Siehe die gleichlautende Stelle im Benutzerbereich: erst herausziehen, dann setzen.
      const api = fehler instanceof ApiError ? fehler : null;
      if (api !== null && api.status === 422) {
        const { felder, unzuordenbar } = feldFehlerAus(api, ["pattern"]);
        const amMuster = felder.pattern;
        if (amMuster !== undefined) setFeldFehler((f) => ({ ...f, [z.id]: amMuster }));
        if (unzuordenbar || amMuster === undefined) {
          const text = api.message;
          setMeldungen((m) => ({ ...m, [z.id]: text }));
        }
      } else if (api !== null && api.status === 409) {
        const von = api.conflict?.changedBy ?? "Jemand";
        const wann = api.conflict?.changedAt ?? "";
        const fremd = alsNummernkreis(api.conflict?.current);
        setKonflikte((k) => ({ ...k, [z.id]: { von, wann, fremd } }));
      } else {
        const text = meldungAus(fehler, "Unerwarteter Fehler beim Speichern.");
        setMeldungen((m) => ({ ...m, [z.id]: text }));
      }
    } finally {
      setSpeichert(null);
    }
  }

  function startwertVon(z: Nummernkreis): string {
    return startEntwuerfe[z.id] ?? String(z.startValue);
  }

  /**
   * Der Startwert geht in einem EIGENEN Aufruf an dieselbe Route (`setNumberRangeStart`).
   * Nicht zusammen mit dem Muster: Der Vertrag trägt genau eine der beiden Gestalten je
   * Rumpf (sonst 400), und beides zusammen wären ohnehin zwei Schreibvorgänge — der
   * zweite liefe gegen den Zeitstempel, den der erste gerade verstellt hat, und der
   * Aufrufer bekäme einen Konflikt mit sich selbst.
   */
  async function startwertSpeichern(z: Nummernkreis): Promise<void> {
    setStartFehler((f) => ({ ...f, [z.id]: "" }));
    setMeldungen((m) => ({ ...m, [z.id]: "" }));
    const zahl = alsZahl(startwertVon(z));
    if (zahl === KEINE_ZAHL || zahl === null) {
      setStartFehler((f) => ({ ...f, [z.id]: "Bitte eine Zahl eintragen." }));
      return;
    }
    setSpeichert(z.id);
    try {
      const neu = await client.setNumberRangeStart(z.id, z.updatedAt, zahl);
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === neu.id ? neu : x))));
      setStartEntwuerfe((e) => { const rest = { ...e }; delete rest[z.id]; return rest; });
    } catch (fehler) {
      const api = fehler instanceof ApiError ? fehler : null;
      if (api !== null && api.status === 422) {
        // NUMMERN_BEREITS_VERGEBEN kommt am Feld `startValue` — genau dort soll die
        // Sperre auch stehen, nicht als Sammelmeldung an der Zeile.
        const { felder, unzuordenbar } = feldFehlerAus(api, ["startValue"]);
        const amFeld = felder.startValue;
        if (amFeld !== undefined) setStartFehler((f) => ({ ...f, [z.id]: amFeld }));
        if (unzuordenbar || amFeld === undefined) {
          const text = api.message;
          setMeldungen((m) => ({ ...m, [z.id]: text }));
        }
      } else if (api !== null && api.status === 409) {
        const von = api.conflict?.changedBy ?? "Jemand";
        const wann = api.conflict?.changedAt ?? "";
        const fremd = alsNummernkreis(api.conflict?.current);
        setKonflikte((k) => ({ ...k, [z.id]: { von, wann, fremd } }));
      } else {
        const text = meldungAus(fehler, "Unerwarteter Fehler beim Speichern.");
        setMeldungen((m) => ({ ...m, [z.id]: text }));
      }
    } finally {
      setSpeichert(null);
    }
  }

  function fremdenStandUebernehmen(id: string): void {
    const fremd = konflikte[id]?.fremd ?? null;
    if (fremd !== null) {
      setZeilen((alt) => (alt === null ? alt : alt.map((x) => (x.id === id ? fremd : x))));
      vergiss(id);
      setStartEntwuerfe((e) => { const rest = { ...e }; delete rest[id]; return rest; });
    }
    setKonflikte((k) => { const rest = { ...k }; delete rest[id]; return rest; });
  }

  if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
  if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;

  return (
    <ul className="vw__liste">
      {zeilen.map((z) => {
        const fehlerText = feldFehler[z.id] ?? "";
        const startFehlerText = startFehler[z.id] ?? "";
        const meldung = meldungen[z.id] ?? "";
        const konflikt = konflikte[z.id];
        const muster = musterVon(z);
        return (
          <li key={z.id} className="vw__eintrag">
            <div className="vw__eintrag-kopf">
              <span className="vw__eintrag-name">
                {z.companyShortCode} · {ENTITY_LABEL[z.entity]}
              </span>
            </div>
            <Feld id={`vw-nr-${z.id}`} label="Muster" bearbeitbar wert={muster}
              onChange={(w) => {
                setEntwuerfe((e) => ({ ...e, [z.id]: w }));
                setFeldFehler((f) => ({ ...f, [z.id]: "" }));
              }}
              {...(fehlerText !== "" ? { fehler: fehlerText } : {})} />
            {/*
              Der Zähler wird ANGEZEIGT, nicht bearbeitet — er gehört dem Nummernvergabe-
              Weg (`allocateNumber`), und ein Eingabefeld hier lüde dazu ein, ihn
              zurückzudrehen und damit bereits vergebene Nummern ein zweites Mal zu
              erzeugen. Deshalb reiner Text, kein deaktiviertes Feld (siehe Feld.tsx).
            */}
            <span className="vw__eintrag-zusatz">Zähler: {z.counter}</span>
            <div className="vw__eintrag-aktionen">
              <button type="button" className="vw__primaer"
                disabled={muster === z.pattern || speichert === z.id}
                aria-label={`Muster für ${z.companyShortCode} ${ENTITY_LABEL[z.entity]} speichern`}
                onClick={() => { void speichern(z); }}>
                Speichern
              </button>
            </div>

            {/*
              Der Startwert: der Zählerstand VOR der ersten Nummer (bei 500 trägt der erste
              Satz die 501) — für die Übernahme aus einem Altsystem. Änderbar NUR, solange
              noch keine Nummer vergeben ist: Danach entstünden Lücken oder Doubletten in
              einer bereits laufenden Nummernfolge (GoBD-Nähe). Ist er gesperrt, steht er
              als TEXT da und nicht als abgeblendetes Feld — mit dem Grund daneben, denn ein
              graues Feld ohne Erklärung sieht aus wie ein Fehler.
            */}
            {z.vergeben ? (
              <>
                <Feld id={`vw-nr-start-${z.id}`} label="Startwert" bearbeitbar={false}
                  wert={String(z.startValue)} onChange={() => undefined} />
                <span className="vw__eintrag-zusatz">
                  Gesperrt: Aus diesem Kreis wurden bereits Nummern vergeben.
                </span>
              </>
            ) : (
              <>
                <Feld id={`vw-nr-start-${z.id}`} label="Startwert" bearbeitbar
                  wert={startwertVon(z)}
                  onChange={(w) => {
                    setStartEntwuerfe((e) => ({ ...e, [z.id]: w }));
                    setStartFehler((f) => ({ ...f, [z.id]: "" }));
                  }}
                  {...(startFehlerText !== "" ? { fehler: startFehlerText } : {})} />
                <div className="vw__eintrag-aktionen">
                  <button type="button" className="vw__knopf"
                    disabled={startwertVon(z) === String(z.startValue)
                      || speichert === z.id}
                    aria-label={`Startwert für ${z.companyShortCode} `
                      + `${ENTITY_LABEL[z.entity]} speichern`}
                    onClick={() => { void startwertSpeichern(z); }}>
                    Startwert speichern
                  </button>
                </div>
              </>
            )}
            {meldung !== "" && <span className="vw__zeilenfehler" role="alert">{meldung}</span>}
            {konflikt !== undefined && (
              <div className="vw__konflikt" role="alert">
                <p className="vw__konflikt-titel">
                  Jemand hat das Muster inzwischen geändert
                  {konflikt.wann === "" ? "" : ` (${konflikt.von}, `
                    + `${formatKonfliktZeit(konflikt.wann)})`}. Deine Eingabe ist nicht
                  verloren — entscheide, welcher Stand gilt.
                </p>
                <button type="button" className="vw__knopf"
                  onClick={() => fremdenStandUebernehmen(z.id)}>
                  Fremden Stand übernehmen
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
